import {useSelector} from 'react-redux'
import {MonthlyPriceTrend} from 'components/Accommodation/MonthlyPriceTrend/MonthlyPriceTrend'
import {getTaxesIncluded} from 'modules/meta/selectors'
import {getHotelAverageRates} from 'modules/seoApi/selectors'

import {Card} from '@vio/atlas/src/components/designSystem/Card'
import {Box} from '@vio/atlas/src/components/designSystem/helpers/Box'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {DatelessCTA, DatelessCTAVariant} from '../DatelessCTA/DatelessCTA'
import {CalendarIcon} from './CalendarIcon'

interface DatelessOfferBoxProps {
  hotelId: string
  hotelName: string
  priceFrom?: number
  variant: 'horizontal' | 'vertical'
}

const PricesSectionTitle = ({hotelName}: {hotelName: string}) => {
  return (
    <Box desktopXs={{paddingBottom: 's400'}}>
      <Text variant="titleL" tag="h2">
        <FormattedMessageWrapper
          id="pricesWithHotelName"
          defaultMessage="Prices for {hotelName}"
          values={{hotelName}}
        />
      </Text>
    </Box>
  )
}

export const DatelessOfferBoxV2 = ({
  hotelId,
  hotelName,
  priceFrom,
  variant
}: DatelessOfferBoxProps) => {
  const averageRates = useSelector(state =>
    getHotelAverageRates(state, hotelId)
  )
  const showTotalPrices = useSelector(getTaxesIncluded)

  if (variant === 'horizontal') {
    return (
      <>
        {averageRates && <PricesSectionTitle hotelName={hotelName} />}
        <Box
          data-id="datelessOfferBox"
          backgroundColor="background.brand.tertiary"
          borderRadius="xl"
          padding="s400"
          gap="s400"
          display="flex"
          alignItems="stretch"
        >
          <Box
            backgroundColor="background.neutral.primary"
            borderStyle="solid"
            borderRadius="lg"
            borderColor="border.brand.primary"
            borderWidth="bw200"
            padding="s600"
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            flexDirection={averageRates ? 'column' : 'row'}
            minHeight={averageRates ? '250px' : 'auto'}
            flex={averageRates ? '0 1 50%' : '1'}
          >
            <Box display="flex" alignItems="center" gap="s400">
              <div>
                <CalendarIcon />
              </div>
              <Text variant="titleM" tag="p">
                <FormattedMessageWrapper
                  id="datelessOfferBox.title"
                  defaultMessage="Select dates to see prices and availability"
                />
              </Text>
            </Box>
            <Box minWidth={averageRates ? '100%' : '256px'}>
              <DatelessCTA
                variant={DatelessCTAVariant.DesktopOfferBoxCTA}
                hotelId={hotelId}
                priceFrom={priceFrom}
              />
            </Box>
          </Box>

          {averageRates && (
            <Box flex="0 1 50%">
              <Card
                borderRadius="lg"
                borderColorPath="border.neutral.secondary"
              >
                <Box padding="s600">
                  <MonthlyPriceTrend
                    hotelName={hotelName}
                    averageRate={averageRates}
                    showTotalPrices={showTotalPrices}
                  />
                </Box>
              </Card>
            </Box>
          )}
        </Box>
      </>
    )
  }

  return (
    <>
      {averageRates && <PricesSectionTitle hotelName={hotelName} />}
      <Card
        dataId="datelessOfferBox"
        borderRadius="lg"
        borderColorPath="border.neutral.secondary"
        isOverflowHidden={false}
      >
        <Box
          margin="-1px"
          backgroundColor="background.brand.tertiary"
          borderRadius="lg"
          borderStyle="solid"
          borderWidth="bw100"
          borderColor="border.brand.tertiary"
        >
          <DatelessCTA
            variant={DatelessCTAVariant.OfferBoxCTA}
            hotelId={hotelId}
            priceFrom={priceFrom}
          />
        </Box>
        {averageRates && (
          <Box padding="s500">
            <MonthlyPriceTrend
              averageRate={averageRates}
              hotelName={hotelName}
              showTotalPrices={showTotalPrices}
            />
          </Box>
        )}
      </Card>
    </>
  )
}
